import { useSelf } from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as applicationActions from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.actions";
import {
  ICreateGroupAction,
  IGroupActionMessage,
} from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.types";
import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import {
  EGroupActionRoleType,
  TGroupActionsLocale as TLocale,
} from "@rentiohq/shared-frontend/dist/types/groupAction.types";
import {
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Modal } from "@rentiohq/web-shared/dist/components";
import { EPreferencePersistScope } from "@rentiohq/web-shared/dist/redux/system/system.types";
import { IAction } from "@rentiohq/web-shared/dist/types";
import { isEmpty } from "lodash";
import React from "react";
import { useDispatch } from "react-redux";
import usePreference from "scenes/Settings/hooks/usePreference";
import { ts } from "../../../../../../services";
import AddGroupActionContent from "./components/AddGroupActionContent";
import SelectGroupAction from "./components/SelectGroupAction";
import {
  getLocalizedGroupActionsContent,
  stepsForType,
} from "./GroupActions.util";
import {
  EGroupActionModalStep,
  EGroupActionType,
} from "./GroupActionsModal.types";

export const PREFERENCE_KEY_GROUP_ACTIONS_PROPERTY_IDS =
  "PREFERENCE_KEY_GROUP_ACTIONS_PROPERTY_IDS_V2";

export interface IGroupActionsModalProps {
  selectedApplicants: IApplication[];
  property: IProperty;
  onConfirm: () => void;
  onCancel: () => void;
}

const GroupActionsModal = (props: IGroupActionsModalProps) => {
  const {
    selectedApplicants,
    property,
    onCancel,
    onConfirm: onModelConfirm,
  } = props;

  const { broker, user } = useSelf();

  // State
  const [isDisabled, setIsDisabled] = React.useState(true);
  const [type, setType] = React.useState<EGroupActionType>();
  const [step, setStep] = React.useState(EGroupActionModalStep.SelectAction);
  const [contents, setContents] = React.useState<Record<TLocale, string>>();
  const [error, setError] = React.useState<string | undefined>();
  const [pushTitle, setPushTitle] = React.useState<Record<TLocale, string>>({
    nl_BE: "",
    fr_FR: "",
    en_UK: "",
  });
  const [emailSubject, setEmailSubject] = React.useState<
    Record<TLocale, string>
  >({ nl_BE: "", fr_FR: "", en_UK: "" });

  const dispatch = useDispatch();

  const [_, setGroupActionPropertyIds] = usePreference<number[]>({
    preferenceKey: PREFERENCE_KEY_GROUP_ACTIONS_PROPERTY_IDS,
    preferencePersistScope: EPreferencePersistScope.InMemory,
  });

  // Effects
  React.useEffect(() => {
    setIsDisabled(!type);
  }, [type]);

  React.useEffect(() => {
    if (step === EGroupActionModalStep.AddContent) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [step]);

  React.useEffect(() => {
    if (step === EGroupActionModalStep.AddContent && !error) {
      if (type === EGroupActionType.Email) {
        const isEmailSubject =
          !isEmpty(emailSubject.en_UK) &&
          !isEmpty(emailSubject.nl_BE) &&
          !isEmpty(emailSubject.fr_FR);

        setIsDisabled(!isEmailSubject);

        return;
      }

      setIsDisabled(false);
    } else if (step === EGroupActionModalStep.AddContent && error) {
      setIsDisabled(true);
    }
  }, [error, emailSubject, pushTitle]);

  // Helpers
  const dispatchGroupAction = (data: ICreateGroupAction) => {
    dispatch(
      applicationActions.applicationGroupActionsStart.getAction({
        data,
        onSuccess(result) {
          onModelConfirm();
        },
      }),
    );
  };

  const handleSendMessage = (
    message: Partial<IGroupActionMessage>,
    roleType: EGroupActionRoleType,
  ) => {
    const { body } = message;

    if (!type || !body || !selectedApplicants.length) {
      return;
    }

    const data: ICreateGroupAction = {
      propertyIds: [property.id],
      applicationIds: selectedApplicants.map(application => application.id),
      role: {
        recipient: [EPropertyMemberTypes.Applicant],
      },
      type,
      groupMessage: { ...message, body },
    };

    dispatchGroupAction(data);
  };

  const handleFinished = () => {
    // Handle finished action
    setGroupActionPropertyIds([property.id]);

    switch (type) {
      case EGroupActionType.Sms:
        handleSendMessage({ body: contents }, EGroupActionRoleType.recipient);

        break;

      case EGroupActionType.Email:
        handleSendMessage(
          { body: contents, subject: emailSubject },
          EGroupActionRoleType.recipient,
        );

        break;

      default:
        break;
    }
  };

  const updateStep = (next: boolean = true) => {
    if (!type) {
      setStep(EGroupActionModalStep.SelectAction);
      return;
    }

    const steps = stepsForType[type];
    const currentIndex = steps.indexOf(step);
    if (currentIndex === -1) return;

    const newIndex = next ? currentIndex + 1 : currentIndex - 1;
    if (newIndex < 0 || newIndex >= steps.length) {
      handleFinished();
    } else {
      setStep(steps[newIndex]);
    }
  };

  // Events
  const onPrevious = () => {
    updateStep(false);
  };

  const onConfirm = () => {
    updateStep();
  };

  const onChangeAction = (selectedType: EGroupActionType) => {
    setType(selectedType);
  };

  // Actions
  const modalActions: IAction[] = [
    {
      content:
        step === EGroupActionModalStep.SelectAction
          ? ts.system("cancel")
          : ts.system("previous"),
      type: "button",
      onClick:
        step === EGroupActionModalStep.SelectAction ? onCancel : onPrevious,
      appearance: "outline",
    },
    {
      content:
        step === EGroupActionModalStep.AddContent
          ? ts.system("save")
          : ts.system("next"),
      type: "button",
      onClick: onConfirm,
      appearance: "primary",
      isDisabled,
    },
  ];

  // Renders
  const renderModalSteps = () => {
    if (step === EGroupActionModalStep.AddContent) {
      return (
        type && (
          <AddGroupActionContent
            type={type}
            initialContents={getLocalizedGroupActionsContent(
              type,
              broker,
              user,
            )}
            onChange={contents => setContents(contents)}
            onChangeError={error => setError(error)}
            onChangeSubject={(key, value) => {
              setEmailSubject(prevPushTitle => ({
                ...prevPushTitle,
                [key]: value,
              }));
            }}
            onChangeTitle={(key, value) => {
              setPushTitle(prevPushTitle => ({
                ...prevPushTitle,
                [key]: value,
              }));
            }}
          />
        )
      );
    }

    return <SelectGroupAction type={type} onChange={onChangeAction} />;
  };

  return (
    <Modal
      onClose={onCancel}
      heading={getLocalizedText("group_actions.modal.heading")}
      hasDismiss={true}
      shouldCloseOnOverlayClick={false}
      width="medium"
      actions={modalActions}
    >
      {renderModalSteps()}
    </Modal>
  );
};

// eslint-disable-next-line import/no-default-export
export default GroupActionsModal;
